import { useAuth } from './useAuth'

const API_ENDPOINT = import.meta.env.VITE_API_ENDPOINT

/**
 * Base API client hook with authentication and 401 retry logic
 */
export function useApi() {
  const { getValidIdToken } = useAuth()

  async function apiRequest(path, options = {}) {
    // Get valid token (will refresh if expired)
    const token = await getValidIdToken()
    if (!token) {
      throw new Error('Not authenticated')
    }

    const url = `${API_ENDPOINT}${path}`

    let response = await fetch(url, {
      ...options,
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
        ...options.headers
      }
    })

    // If 401, refresh token and retry ONCE
    if (response.status === 401) {
      console.log('Received 401, refreshing token and retrying...')
      const freshToken = await getValidIdToken()

      if (!freshToken) {
        throw new Error('Authentication failed')
      }

      // Retry the request with fresh token
      response = await fetch(url, {
        ...options,
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${freshToken}`,
          ...options.headers
        }
      })
    }

    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: 'Request failed' }))
      throw new Error(error.error || `HTTP ${response.status}`)
    }

    return response.json()
  }

  async function get(path) {
    return apiRequest(path, { method: 'GET' })
  }

  async function post(path, data) {
    return apiRequest(path, {
      method: 'POST',
      body: JSON.stringify(data)
    })
  }

  async function put(path, data) {
    return apiRequest(path, {
      method: 'PUT',
      body: JSON.stringify(data)
    })
  }

  async function del(path) {
    return apiRequest(path, { method: 'DELETE' })
  }

  return { get, post, put, delete: del }
}
